import React, { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import CompaniesStats from '../companies/CompaniesStats';
import CompanyStats from '../companies/CompanyStats';
import MainLayout from '../../shared/components/MainLayout';
import ShareLinkInvalid from './ShareLinkInvalid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  clearShareToken,
  markTokenMissing,
  selectTokenReadyStatus,
  setShareToken,
} from '../../store/slices/shareSlice';
import { shareApi } from '../../store/services/DashboardShareService';
import { isValidUuid } from '../../shared/utils/utils';
import type { RootState } from '../../store/store';

const ShareDashboardEntry: React.FC = () => {
  const dispatch = useAppDispatch();
  const tokenReadyStatus = useAppSelector(selectTokenReadyStatus);
  // Route is mounted via splat `/share/dashboard/*`; `useMatch` extracts
  // the drill-down companyId without unmounting on overview ↔ drill-down.
  const drilldownMatch = useMatch('/share/dashboard/:companyId');
  const companyId = drilldownMatch?.params.companyId;

  // useState's lazy initializer fires inside render, before children mount.
  useState(() => {
    if (tokenReadyStatus !== 'pending') return;
    const hash = window.location.hash.replace(/^#/, '').trim();
    if (hash.startsWith('dst_')) dispatch(setShareToken(hash));
    else dispatch(markTokenMissing());
  });

  // Reset on unmount so an already-authed admin who opens a share link in
  // their tab isn't trapped in viewer mode on every subsequent admin route.
  useEffect(() => () => { dispatch(clearShareToken()); }, [dispatch]);

  // Watch every share-api query for a 401. Args-keyed `useQueryState` would
  // miss errors landing on cache entries with different args (e.g. periods).
  const isUnauthorized = useAppSelector((state: RootState) => {
    const queries = state[shareApi.reducerPath]?.queries ?? {};
    for (const key of Object.keys(queries)) {
      const q = queries[key];
      if (q && q.error && typeof q.error === 'object' && 'status' in q.error
        && (q.error as { status: unknown }).status === 401) {
        return true;
      }
    }
    return false;
  });

  if (tokenReadyStatus === 'pending') return null;
  if (tokenReadyStatus === 'missing') return <ShareLinkInvalid reason="missing" />;
  if (isUnauthorized) return <ShareLinkInvalid reason="expired" />;

  if (companyId) {
    if (!isValidUuid(companyId)) return <ShareLinkInvalid reason="missing" />;
    return (
      <MainLayout>
        <CompanyStats companyId={companyId} />
      </MainLayout>
    );
  }
  return <CompaniesStats />;
};

export default ShareDashboardEntry;
